import { Icon } from '@iconify/react';
import { TenancyStatusBadge, TenancyActions, formatDate, memberDisplayName, memberInitials } from './tenancyStatus';

const AVATAR_COLORS = [
    'tw-bg-blue-500', 'tw-bg-emerald-500', 'tw-bg-purple-500', 'tw-bg-pink-500',
    'tw-bg-orange-500', 'tw-bg-teal-500', 'tw-bg-indigo-500', 'tw-bg-rose-500',
    'tw-bg-cyan-500', 'tw-bg-amber-500', 'tw-bg-lime-600', 'tw-bg-fuchsia-500', 'tw-bg-violet-500'
];


const getAvatarColor = (id) => {
    return AVATAR_COLORS[(id || 0) % AVATAR_COLORS.length];
};

const getRoleLabel = (role) => {
    if (!role) return '';
    return role.charAt(0) + role.slice(1).toLowerCase();
};

const MemberTable = ({ members, onView, onEdit, onDelete, onToggleStatus, onHistory, showTenancy = false }) => {
    return (
        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
            <table className="tw-w-full tw-text-sm">
                <thead>
                    <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                        <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Member</th>
                        <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Role</th>
                        <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Unit</th>
                        <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Contact</th>
                        <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">
                            {members?.[0]?.is_primary !== undefined ? 'Primary' : 'Resident'}
                        </th>
                        <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Start Date</th>
                        {showTenancy && (
                            <>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Agreement End</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">End Date</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Status</th>
                            </>
                        )}
                        <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {members.map((member) => {
                        const fullName = memberDisplayName(member);
                        const flatNumber = member.flat?.flat_number || '-';
                        const wingTitle = member.flat?.wing?.title || '-';

                        return (
                            <tr key={member.flat_ownership_id || member.occupant_id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50">
                                <td className="tw-px-3 tw-py-2">
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        <div className={`tw-w-8 tw-h-8 tw-rounded-full ${getAvatarColor(member.flat_ownership_id || member.occupant_id)} tw-flex tw-items-center tw-justify-center`}>
                                            <span className="tw-text-white tw-text-xs tw-font-semibold">
                                                {memberInitials(member)}
                                            </span>
                                        </div>
                                        <span className="tw-font-medium tw-text-gray-800">{fullName}</span>
                                    </div>
                                </td>
                                <td className="tw-px-3 tw-py-2 tw-text-gray-600">{getRoleLabel(member.role)}</td>
                                <td className="tw-px-3 tw-py-2">
                                    <p className="tw-font-medium tw-text-gray-800">{flatNumber}</p>
                                    <p className="tw-text-[10px] tw-text-gray-500">{wingTitle}</p>
                                </td>
                                <td className="tw-px-3 tw-py-2">
                                    <p className="tw-text-gray-600">{member.phone || '-'}</p>
                                    <p className="tw-text-[10px] tw-text-gray-500 tw-truncate tw-max-w-[150px]">{member.email || '-'}</p>
                                </td>
                                <td className="tw-px-3 tw-py-2">
                                    {member.is_primary !== undefined ? (
                                        <div className="tw-flex tw-flex-col tw-gap-0.5">
                                            <span className={`tw-text-[10px] tw-font-medium tw-px-1.5 tw-py-0.5 tw-rounded-full tw-w-fit ${member.is_primary ? 'tw-bg-blue-100 tw-text-blue-700' : 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                {member.is_primary ? 'Primary' : 'Co-Owner'}
                                            </span>
                                            {member.ownership_share !== undefined && member.ownership_share !== null && (
                                                <span className="tw-text-[10px] tw-text-gray-500">
                                                    {parseFloat(member.ownership_share)}% share
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className={`tw-font-medium ${member.is_resident ? 'tw-text-emerald-600' : 'tw-text-gray-500'}`}>
                                            {member.is_resident ? 'Yes' : 'No'}
                                        </span>
                                    )}
                                </td>
                                {/* Start date only. The agreement end date used to sit under it as
                                    well, beside a separate (usually empty) End Date column - the two
                                    dates now have a column each. */}
                                <td className="tw-px-3 tw-py-2 tw-text-gray-600">{formatDate(member.start_date)}</td>
                                {showTenancy && (
                                    <>
                                        {/* When the rent agreement runs out. */}
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-600">
                                            {member.agreement_end_date
                                                ? formatDate(member.agreement_end_date)
                                                : <span className="tw-text-gray-300" title="No agreement end date recorded">Not set</span>}
                                        </td>
                                        {/* When the tenant moved (or moves) out - empty while the tenancy runs. */}
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-600">
                                            {member.end_date
                                                ? formatDate(member.end_date)
                                                : <span className="tw-text-gray-300" title="Set when the tenant moves out">&mdash;</span>}
                                        </td>
                                        <td className="tw-px-3 tw-py-2"><TenancyStatusBadge member={member} /></td>
                                    </>
                                )}
                                <td className="tw-px-3 tw-py-2">
                                    <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                        <button onClick={() => onView?.(member)} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600" title="View">
                                            <Icon icon="mdi:eye-outline" />
                                        </button>
                                        <button onClick={() => onEdit?.(member)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400" title="Edit">
                                            <Icon icon="mdi:pencil-outline" />
                                        </button>
                                        {showTenancy && (
                                            <TenancyActions member={member} onToggleStatus={onToggleStatus} onHistory={onHistory} />
                                        )}
                                        <button onClick={() => onDelete?.(member)} className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-600" title="Delete">
                                            <Icon icon="mdi:delete-outline" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {members.length === 0 && (
                <div className="tw-text-center tw-py-8">
                    <Icon icon="mdi:account-group-outline" className="tw-text-3xl tw-text-gray-300" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No members found</p>
                </div>
            )}
        </div>
    );
};

export default MemberTable;
